import { useEffect, useState } from 'react';
import { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Navbar from '../components/Navbar';
import ImageCarousel from '../components/ImageCarousel';
import StaffSection from '../components/StaffSection';
import HeroSection from '../components/HeroSection';
import ReviewsSection from '../components/ReviewsSection';

// Custom Arrow Components for Doctor Carousel
const ArrowLeft = (props: any) => (
  <button
    {...props}
    className="slick-arrow slick-prev !z-50 absolute top-1/2 -translate-y-1/2 left-[-32px] md:left-[-48px] w-12 h-12 md:w-14 md:h-14 bg-primary border-4 border-primary-dark shadow-2xl hover:bg-primary-dark text-white rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none"
    aria-label="Previous"
    style={{ ...props.style, display: 'block' }}
  >
    <svg className="w-7 h-7 md:w-8 md:h-8 font-bold" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  </button>
);
const ArrowRight = (props: any) => (
  <button
    {...props}
    className="slick-arrow slick-next !z-50 absolute top-1/2 -translate-y-1/2 right-[-32px] md:right-[-48px] w-12 h-12 md:w-14 md:h-14 bg-primary border-4 border-primary-dark shadow-2xl hover:bg-primary-dark text-white rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none"
    aria-label="Next"
    style={{ ...props.style, display: 'block' }}
  >
    <svg className="w-7 h-7 md:w-8 md:h-8 font-bold" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  </button>
);

// Custom Arrow Components for Services Carousel - Big, round, light blue (main color)
const BLUE = '#2563eb'; // A strong blue

const ServicesArrowLeft = (props: any) => (
  <button
    {...props}
    className="slick-arrow slick-prev !z-50 absolute top-1/2 -translate-y-1/2 left-[-40px] md:left-[-56px] w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none shadow-lg hover:scale-110 hover:shadow-2xl"
    aria-label="Previous"
    style={{ ...props.style, display: 'block', backgroundColor: BLUE, border: 'none' }}
  >
    <svg className="w-12 h-12 md:w-16 md:h-16" fill="none" stroke="white" strokeWidth="4" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  </button>
);
const ServicesArrowRight = (props: any) => (
  <button
    {...props}
    className="slick-arrow slick-next !z-50 absolute top-1/2 -translate-y-1/2 right-[-40px] md:right-[-56px] w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none shadow-lg hover:scale-110 hover:shadow-2xl"
    aria-label="Next"
    style={{ ...props.style, display: 'block', backgroundColor: BLUE, border: 'none' }}
  >
    <svg className="w-12 h-12 md:w-16 md:h-16" fill="none" stroke="white" strokeWidth="4" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  </button>
);

// Add interface for staff member type
interface StaffMember {
  id: string;
  name: string;
  title: string;
  description: string;
  image: string;
}

const StaffCard = ({ member }: { member: StaffMember }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const maxLength = 150;
  const shouldShowButton = member.description.length > maxLength;

  return (
    <div className="h-[500px] mx-2 bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-1 flex flex-col">
      {/* Staff Image */}
      <div 
        className={`relative transition-all duration-300 ${
          isExpanded ? 'h-24' : 'h-64'
        }`}
      >
        <img
          src={member.image}
          alt={member.name}
          className="w-full h-full object-cover"
        />
      </div>
      {/* Staff Info */}
      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-xl font-bold text-primary-dark mb-2">
          {member.name}
        </h3>
        <h4 className="text-lg text-gray-600 mb-3">
          {member.title}
        </h4>
        <div className="flex-1">
          <p className={`text-gray-500 text-sm ${!isExpanded && 'line-clamp-3'}`}>
            {member.description}
          </p>
          {shouldShowButton && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-primary-dark font-semibold text-sm hover:text-primary-dark/80 transition-colors"
            >
              {isExpanded ? t('staff.seeLess') : t('staff.seeMore')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Add at the top of the file, after StaffCard:
const DoctorCard = ({ doctor, t }: { doctor: StaffMember, t: any }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 150;
  const shouldShowButton = doctor.description.length > maxLength;

  return (
    <div className={`bg-white rounded-3xl p-8 transition-all duration-300 transform hover:-translate-y-2 border-2 border-primary flex flex-col ${isExpanded ? 'h-[500px]' : 'h-[370px]'}`}>
      <div className={`relative transition-all duration-300 ${isExpanded ? 'h-24' : 'h-32'} flex justify-center items-center mb-6`}> {/* slightly smaller image than staff */}
        <img
          src={doctor.image}
          alt={doctor.name}
          className="w-24 h-24 rounded-full object-cover mx-auto shadow-lg border-4 border-primary/10"
        />
      </div>
      <div className="flex-1 flex flex-col">
        <h4 className="text-xl font-bold text-gray-900 mb-2">{doctor.name}</h4>
        <p className="text-primary-dark font-semibold mb-4">{doctor.title}</p>
        <div className="flex-1">
          <p className={`text-gray-600 text-sm leading-relaxed ${!isExpanded && 'line-clamp-3'}`}>{doctor.description}</p>
          {shouldShowButton && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-primary-dark font-semibold text-sm hover:text-primary-dark/80 transition-colors"
            >
              {isExpanded ? t('staff.seeLess') : t('staff.seeMore')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Staff-style arrows matching ImageCarousel
const StaffArrowLeft = (props: any) => (
  <button
    {...props}
    className="slick-arrow slick-prev !z-50 absolute top-1/2 -translate-y-1/2 left-[-40px] md:left-[-56px] w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none shadow-lg hover:scale-110 hover:shadow-2xl"
    aria-label="Previous"
    style={{ ...props.style, display: 'block', backgroundColor: BLUE, border: 'none' }}
  >
    <svg className="w-12 h-12 md:w-16 md:h-16" fill="none" stroke="white" strokeWidth="4" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  </button>
);
const StaffArrowRight = (props: any) => (
  <button
    {...props}
    className="slick-arrow slick-next !z-50 absolute top-1/2 -translate-y-1/2 right-[-40px] md:right-[-56px] w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none shadow-lg hover:scale-110 hover:shadow-2xl"
    aria-label="Next"
    style={{ ...props.style, display: 'block', backgroundColor: BLUE, border: 'none' }}
  >
    <svg className="w-12 h-12 md:w-16 md:h-16" fill="none" stroke="white" strokeWidth="4" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  </button>
);

// Staff carousel arrows - using same design as Services arrows
const desktopSliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  rtl: false, // will be set dynamically if needed
  nextArrow: <StaffArrowRight />,
  prevArrow: <StaffArrowLeft />,
  appendDots: (dots: any) => (
    <div style={{ marginTop: '2.5rem' }}>
      <ul style={{ margin: '0px' }}> {dots} </ul>
    </div>
  ),
  responsive: [
    {
      breakpoint: 1280,
      settings: {
        slidesToShow: 3,
      }
    },
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
      }
    },
    {
      breakpoint: 640,
      settings: {
        slidesToShow: 1,
      }
    }
  ]
};

const mobileSliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  rtl: false, // Force LTR for consistent mobile experience
  nextArrow: <StaffArrowRight />,
  prevArrow: <StaffArrowLeft />,
  appendDots: (dots: any) => (
    <div style={{ marginTop: '2.5rem' }}>
      <ul style={{ margin: '0px' }}> {dots} </ul>
    </div>
  ),
};

const StaffGrid = ({ members }: { members: StaffMember[] }) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  // Always show carousel on mobile if more than 1 member
  // On desktop, show grid if 4 or fewer members, carousel if more
  return (
    <>
      {/* Mobile: Always carousel if more than 1 member */}
      <div className="md:hidden">
        {members.length > 1 ? (
          <div className="relative px-4 mb-24">
            <div className="relative">
              <Slider {...mobileSliderSettings}>
                {members.map(member => (
                  <div key={member.id} className="px-2">
                    <StaffCard member={member} />
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8">
            {members.map(member => (
              <StaffCard key={member.id} member={member} />
            ))}
          </div>
        )}
      </div>

      {/* Desktop: Grid if 4 or fewer, carousel if more */}
      <div className="hidden md:block">
        {members.length <= 4 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {members.map(member => (
              <StaffCard key={member.id} member={member} />
            ))}
          </div>
        ) : (
          <div className="relative px-4 mb-24">
            <div className="relative">
              <Slider {...desktopSliderSettings}>
                {members.map(member => (
                  <div key={member.id} className="px-2">
                    <StaffCard member={member} />
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// Add this helper function above the Home component
const splitDayAndHours = (str: string) => {
  const idx = str.indexOf(':');
  if (idx === -1) return { day: str, hours: '' };
  return {
    day: str.slice(0, idx + 1),
    hours: str.slice(idx + 1).trim()
  };
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentServiceSlide, setCurrentServiceSlide] = useState(0);
  const [currentStaffSlide, setCurrentStaffSlide] = useState(0);
  const [currentDoctorSlide, setCurrentDoctorSlide] = useState(0);
  const [currentOfficeSlide, setCurrentOfficeSlide] = useState(0);
  const [currentProDocSlide, setCurrentProDocSlide] = useState(0);
  const [currentAdminSlide, setCurrentAdminSlide] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      alert(t('contact.form.validationError') || 'Please fill in all required fields.');
      return;
    }
    
    // Create email content with all form data
    const subject = encodeURIComponent(`Contact Form Submission from ${formData.name}`);
    const body = encodeURIComponent(`
Dear SMC Team,

You have received a new contact form submission from the SMC website:

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CONTACT INFORMATION:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
MESSAGE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${formData.message}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
FORM DETAILS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Submission Date: ${new Date().toLocaleString()}
Website Language: ${i18n.language}
User Agent: ${navigator.userAgent}

---
This message was automatically generated from the SMC website contact form.
Please respond to the customer at: ${formData.email}
    `);
    
    // Create mailto link with all the information
    const mailtoLink = `mailto:smc.shefaram@gmail.com?subject=${subject}&body=${body}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
  };

  const { ref: heroStatsRef, inView: heroStatsInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [activeTab, setActiveTab] = useState<'about' | 'vision'>('about');
  const [activeStaffCategory, setActiveStaffCategory] = useState('management');
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const [counts, setCounts] = useState<number[]>([0, 0, 0, 0]);

  // Define service-doctor mappings (using 'p_' prefix for paramedical staff IDs to avoid duplicate keys)
  const serviceDoctors: { [key: string]: string[] } = {
    cardiology: [
      "m2", // Dr. Salim Adwi
      "m3", // Dr. Nader Hadar
      "m4", // Dr. Nemer Samania
      "m5", // Dr. Assad Khoury (children)
      "m6", // Dr. Grace Shahada
      "m11", // Dr. Elias Azam
      "m12", // Dr. Hussain Suleiman
      "p_m2", // Ihab Asmir
      "p_m3", // Magdi Ghanaim
      "p_m4", // Osama Sheikh Ahmed
      "p_m6", // Nermin
    ],
    pediatricCardiology: [
      "m5", // Dr. Assad Khoury
    ],
    ent: ["m7"],
    ophthalmology: ["m8"],
    occupational: ["m1"],
    gynecology: ["m9"],
    hearing: ["p_m1"],
    dental: ["m10"],
  };

  // Update getDoctorsForService to handle 'p_' prefix for paramedical staff
  const getDoctorsForService = (serviceId: string) => {
    const doctorIds = serviceDoctors[serviceId] || [];
    const allMedicalDoctors = t('staff.members.medical', { returnObjects: true }) as StaffMember[];
    const allParamedicalStaff = t('staff.members.paramedical', { returnObjects: true }) as StaffMember[];

    return doctorIds.map(id => {
      if (id.startsWith('p_')) {
        // paramedical staff
        return allParamedicalStaff.find(staff => `p_${staff.id}` === id);
      } else {
        // medical staff
        return allMedicalDoctors.find(staff => staff.id === id);
      }
    }).filter(Boolean);
  };

  const services = [
    {
      id: 'occupational',
      icon: '🏥',
      gradient: 'from-blue-500 to-cyan-500',
      bgGradient: 'from-blue-50 to-cyan-50'
    },
    {
      id: 'cardiology',
      icon: '❤️',
      gradient: 'from-red-500 to-pink-500',
      bgGradient: 'from-red-50 to-pink-50'
    },
    {
      id: 'pediatricCardiology',
      icon: '👶',
      gradient: 'from-purple-500 to-indigo-500',
      bgGradient: 'from-purple-50 to-indigo-50'
    },
    {
      id: 'hearing',
      icon: '👂',
      gradient: 'from-green-500 to-emerald-500',
      bgGradient: 'from-green-50 to-emerald-50'
    },
    {
      id: 'ent',
      icon: '👃',
      gradient: 'from-orange-500 to-yellow-500',
      bgGradient: 'from-orange-50 to-yellow-50'
    },
    {
      id: 'dental',
      icon: '🦷',
      gradient: 'from-teal-500 to-blue-500',
      bgGradient: 'from-teal-50 to-blue-50'
    },
    {
      id: 'gynecology',
      icon: '🌸',
      gradient: 'from-rose-500 to-pink-500',
      bgGradient: 'from-rose-50 to-pink-50'
    },
    {
      id: 'ophthalmology',
      icon: '👁️',
      gradient: 'from-violet-500 to-purple-500',
      bgGradient: 'from-violet-50 to-purple-50'
    }
  ];

  useEffect(() => {
    if (heroStatsInView) {
      const timers: NodeJS.Timeout[] = [];
      
      [30, 7, 95, 98].forEach((targetValue, index) => {
        const timer = setInterval(() => {
          setCounts(prev => {
            if (prev[index] >= targetValue) {
              clearInterval(timer);
              return prev;
            }
            const newCounts = [...prev];
            newCounts[index] = Math.min(prev[index] + 1, targetValue);
            return newCounts;
          });
        }, 50);
        
        timers.push(timer);
      });

      return () => timers.forEach(clearInterval);
    }
  }, [heroStatsInView]);

  useEffect(() => {
    const handleSectionChange = () => {
      // Get the current section from the URL hash or from the element in view
      const hash = window.location.hash.replace('#', '');
      // If not on services section, reset selectedService
      if (hash && hash !== 'services') {
        setSelectedService(null);
      } else if (!hash) {
        // If no hash, check scroll position
        const servicesSection = document.getElementById('services');
        if (servicesSection) {
          const rect = servicesSection.getBoundingClientRect();
          // If services section is not in view, reset
          if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
            setSelectedService(null);
          }
        }
      }
    };
    window.addEventListener('hashchange', handleSectionChange);
    window.addEventListener('scroll', () => {
      const servicesSection = document.getElementById('services');
      if (servicesSection) {
        const rect = servicesSection.getBoundingClientRect();
        // Only reset if the section is fully out of view
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
          setSelectedService(null);
        }
      }
    });
    return () => {
      window.removeEventListener('hashchange', handleSectionChange);
      window.removeEventListener('scroll', handleSectionChange);
    };
  }, []);

  const doctorsSliderRef = useRef<any>(null);

  // Reviews carousel settings
  const reviewsSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    rtl: false,
    nextArrow: <ArrowRight />, 
    prevArrow: <ArrowLeft />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  // Reviews data
  const reviews = [
    {
      name: 'מוחמד עומר',
      text: 'עשיתי בדיקת מנוף בתוך המרפאה הכל היה מסודר ומהיר הצוות קיבל אותי בזמן עשו לי את כל הבדיקות בלי סיבוכים בדיקת שמיעה ראייה ולחץ דם השירות היה נעים ואכפתי באמת תודה רבה לכם',
      rating: 4.5
    },
    {
      name: 'חאלד טהא',
      text: 'עשיתי בדיקות לקרדיולוגיה כולל אק"ג ואקו לב השירות היה ברמה גבוהה מאוד הרופא הסביר לי הכל בלי למהר ועם הרבה סבלנות ממש מרגישים שחשוב להם',
      rating: 5
    },
    {
      name: 'ויסאם ג׳ריס',
      text: 'באתי לבדיקה לרישיון מנופאי עשו לי בדיקת שמיעה בדיקת ראייה לחץ דם הכל היה במקום אחד בלי לרוץ בין קליניקות באמת שירות נוח ומסודר ממליץ',
      rating: 4
    },
    {
      name: 'איריס כהן',
      text: 'סבא שלי קיבל מכשיר שמיעה דרך המרכז הרפואי הצוות היה מדהים התאימו לו בדיוק לפי הצורך שלו והסבירו כל דבר בצורה פשוטה ובלי לחץ ממליצה מכל הלב',
      rating: 5
    },
    {
      name: 'אחמד חוג׳ראת',
      text: 'אני עובד במפעל עם חשיפה לרעש והגעתי לבדיקה תעסוקתית בדקו לי שמיעה לחץ דם וכל הבדיקות הדרושות הצוות היה מקצועי מאוד והבדיקות עברו מהר ובצורה מכובדת',
      rating: 4.5
    },
    {
      name: 'רועי בן שושן',
      text: 'עשיתי בדיקה תעסוקתית בגלל חשיפה לרעש במפעל הגיעו עד אלינו עם צוות רפואי מצויד הכל היה מסודר ואחראי ממש חוויה חיובית שלא רואים כל יום',
      rating: 4
    },
    {
      name: 'שחר מזרחי',
      text: 'הגעתי עם כאבי אוזניים חמורים והרופא טיפל בי בסבלנות ובמקצועיות משהו שלא רואים בכל מקום ממליצה מאוד באמת אנשים מדהימים',
      rating: 5
    },
    {
      name: 'סמיר חמדאן',
      text: 'פעם ראשונה שאני מגיע וכל מה שאני יכול להגיד זה תודה שירות מעולה מקום נקי הרופאה של העיניים הייתה סופר נחמדה וקשובה',
      rating: 5
    },
    {
      name: 'נטע לוי',
      text: 'הייתי בבדיקה שגרתית אצל הגניקולוגית ד״ר סוהא והיה פשוט מושלם אין לחץ הם לוקחים את הזמן ועובדים באווירה רגועה ונעימה',
      rating: 4.5
    },
    {
      name: 'אחמד אבו עיאש',
      text: 'באמת מרכז ברמה גבוהה הכל מסודר הנציגים בקבלה מדהימים וגם פרמדיקים עם הרבה ניסיון ממליץ לכל מי שמחפש מקום טוב בצפון',
      rating: 5
    },
  ];

  // Calculate average rating for hero satisfaction percentage
  const ratedReviews = reviews.filter(r => typeof r.rating === 'number');
  const avgRating = ratedReviews.length ? ratedReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / ratedReviews.length : 0;
  const satisfactionPercent = Math.round((avgRating / 5) * 100 / 5) * 5; // round to nearest 5%

  // SVGs for stars
  const FullStar = () => (
    <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" /></svg>
  );
  const HalfStar = () => (
    <svg className="w-6 h-6 text-yellow-500" viewBox="0 0 20 20">
      <defs>
        <linearGradient id="half">
          <stop offset="50%" stopColor="currentColor"/>
          <stop offset="50%" stopColor="white" stopOpacity="1"/>
        </linearGradient>
      </defs>
      <path fill="url(#half)" stroke="currentColor" strokeWidth="0.5" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" />
    </svg>
  );
  const EmptyStar = () => (
    <svg className="w-6 h-6 text-yellow-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" /></svg>
  );

  // ReviewCard component for carousel
  const ReviewCard = ({ review, t }: { review: { name: string; text: string; rating: number | null }, t: any }) => {
    const [expanded, setExpanded] = useState(false);
    const maxLength = 180;
    const isLong = review.text.length > maxLength;
    const displayText = expanded || !isLong ? review.text : review.text.slice(0, maxLength) + '...';
    return (
      <div className="px-2 h-full flex items-stretch">
        <div className="bg-white rounded-xl shadow p-6 flex flex-col justify-between h-full min-h-[320px] max-h-[320px]">
          {/* Stars */}
          {typeof review.rating === 'number' && (
            <div className="flex justify-center mb-2">
              {Array.from({ length: 5 }).map((_, i) => {
                const rating = review.rating ?? 0;
                if (rating >= i + 1) return <FullStar key={i} />;
                if (rating >= i + 0.5) return <HalfStar key={i} />;
                return <EmptyStar key={i} />;
              })}
            </div>
          )}
          {/* Review Text */}
          <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-700 mb-4">
            <span>{displayText}</span>
            {isLong && (
              <button
                className="text-primary-dark underline mt-2 text-sm"
                onClick={() => setExpanded(e => !e)}
              >
                {expanded ? t('reviews.showLess', 'Show less') : t('reviews.readMore', 'Read more')}
              </button>
            )}
          </div>
          {/* Name */}
          <div className="font-bold text-center mt-4 text-primary-dark">
            {review.name}
          </div>
        </div>
      </div>
    );
  };

  // Add new slider settings for doctors-for-service carousel (no custom arrows)
  const doctorsSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    rtl: false, // will be set dynamically if needed
    appendDots: (dots: any) => (
      <div style={{ marginTop: '2.5rem' }}>
        <ul style={{ margin: '0px' }}> {dots} </ul>
      </div>
    ),
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  // Function to handle Waze links for both mobile and desktop
  const handleWazeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const latitude = 32.8005983;
    const longitude = 35.1723365;
    
    // Check if user is on mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // For mobile, use the standard Waze deep link that automatically handles app vs web
      const wazeUrl = `waze://?ll=${latitude},${longitude}&navigate=yes`;
      
      // Use window.open instead of window.location.href for better mobile compatibility
      const newWindow = window.open(wazeUrl, '_blank');
      
      // If window.open fails (blocked by popup blocker), fallback to location.href
      if (!newWindow) {
        window.location.href = wazeUrl;
      }
    } else {
      // Desktop users go directly to web version
      window.open(`https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <Navbar onServicesClick={() => setSelectedService(null)} />
      <main className="pt-16">
        {/* Hero Section */}
        <HeroSection />

        {/* Services Section */}
        <section id="services" className="pt-8 pb-4 md:pt-8 md:pb-4 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 min-h-screen flex flex-col justify-start">
          <div className="container mx-auto px-2 md:px-4 flex-1 flex flex-col justify-start">
            {/* Header */}
            <div className="text-center mb-4 md:mb-6">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-2 md:mb-3">
                {t('services.title')}
              </h2>
              <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-2 md:mb-3">
                {t('services.description')}
              </p>
              <p className="text-xs md:text-sm text-gray-500 max-w-3xl mx-auto mt-1 md:mt-2 leading-relaxed">
                {t('services.partnership')}
              </p>
            </div>

            {/* Services Grid */}
            {!selectedService ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 flex-1 items-stretch" style={{minHeight: 0}}>
                {services.map((service, index) => (
                  <div
                    key={service.id}
                    onClick={() => setSelectedService(service.id)}
                    className="group relative overflow-hidden rounded-xl bg-white shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border-2 cursor-pointer border-primary flex flex-col justify-between min-h-[210px] md:min-h-[220px]"
                  >
                    {/* Background Pattern */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${service.bgGradient} opacity-20`}></div>
                    {/* Service Card Content */}
                    <div className="relative p-3 md:p-4 flex flex-col flex-1 justify-between">
                      {/* Icon */}
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-xl md:text-2xl mb-2 md:mb-3 shadow">
                        {service.icon}
                      </div>
                      {/* Title */}
                      <h3 className="text-base md:text-lg font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-primary-dark transition-colors">
                        {t(`services.${service.id}.title` as any)}
                      </h3>
                      {/* Description */}
                      <p className="text-xs md:text-sm text-gray-600 leading-snug flex-grow line-clamp-3 mb-1">
                        {t(`services.${service.id}.description` as any)}
                      </p>
                      {/* Doctor Count */}
                      <div className="mt-1 flex items-center justify-between">
                        <div className="flex items-center space-x-1 space-x-reverse">
                          <span className="text-xs text-gray-500">
                            {t('services.specialists')}:
                          </span>
                          <span className="bg-primary/10 text-primary-dark px-1.5 py-0.5 rounded-full text-xs font-semibold">
                            {getDoctorsForService(service.id).length}
                          </span>
                        </div>
                        <div className="text-xs text-primary font-semibold">
                          {t('services.viewDoctors')}
                        </div>
                      </div>
                      {/* Hover Effect */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-primary/80 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-full px-4">
                {/* Back Button */}
                <div className="flex justify-between items-center mb-8">
                  <button
                    onClick={() => setSelectedService(null)}
                    className="flex items-center space-x-2 space-x-reverse text-primary-dark hover:text-primary transition-colors font-semibold"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>{t('services.backToServices')}</span>
                  </button>
                </div>

                {/* Selected Service Header */}
                <div className="w-full text-center mb-12 px-2 mx-auto">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-primary to-primary/80 text-3xl mb-6 shadow-lg">
                    {services.find(s => s.id === selectedService)?.icon}
                  </div>
                  <h3 className="text-3xl font-bold text-primary-dark mb-4">
                    {t(`services.${selectedService}.title` as any)}
                  </h3>
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    {t(`services.${selectedService}.description` as any)}
                  </p>
                </div>

                {/* Doctors Grid */}
                {getDoctorsForService(selectedService).length > 0 ? (
                  <>
                    {/* Desktop Carousel for >3 doctors, grid otherwise */}
                    <div className="hidden md:block">
                      {getDoctorsForService(selectedService).length > 3 ? (
                        <div className="relative">
                          <button
                            onClick={() => doctorsSliderRef.current?.slickPrev()}
                            className="bg-white bg-opacity-80 p-3 rounded-full shadow-lg z-10 hover:bg-opacity-100 transition-all absolute left-0 top-1/2 -translate-y-1/2"
                            style={{ left: '-56px' }}
                            aria-label="Previous"
                          >
                            ←
                          </button>
                          <button
                            onClick={() => doctorsSliderRef.current?.slickNext()}
                            className="bg-white bg-opacity-80 p-3 rounded-full shadow-lg z-10 hover:bg-opacity-100 transition-all absolute right-0 top-1/2 -translate-y-1/2"
                            style={{ right: '-56px' }}
                            aria-label="Next"
                          >
                            →
                          </button>
                          <Slider ref={doctorsSliderRef} {...doctorsSliderSettings}>
                            {getDoctorsForService(selectedService).filter(Boolean).map((doctor) => (
                              <div key={doctor!.id} className="px-2">
                                <DoctorCard doctor={doctor!} t={t} />
                              </div>
                            ))}
                          </Slider>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {getDoctorsForService(selectedService).filter(Boolean).map((doctor) => (
                            <div key={doctor!.id}>
                              <DoctorCard doctor={doctor!} t={t} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Mobile Carousel for >1 doctor, single otherwise */}
                    <div className="md:hidden">
                      {getDoctorsForService(selectedService).length > 1 ? (
                        <div className="relative mb-12">
                          <Slider {...mobileSliderSettings}>
                            {getDoctorsForService(selectedService).filter(Boolean).map((doctor) => (
                              <div key={doctor!.id} className="w-full flex justify-center">
                                <DoctorCard doctor={doctor!} t={t} />
                              </div>
                            ))}
                          </Slider>
                        </div>
                      ) : (
                        getDoctorsForService(selectedService).filter(Boolean).map((doctor) => (
                          <div key={doctor!.id} className="w-full flex justify-center mb-12">
                            <DoctorCard doctor={doctor!} t={t} />
                          </div>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-16">
                    <div className="text-gray-400 text-8xl mb-6">👨‍⚕️</div>
                    <h3 className="text-2xl font-bold text-gray-600 mb-4">
                      {t('services.noSpecialists')}
                    </h3>
                    <p className="text-gray-500">
                      {t('services.noSpecialistsDescription')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Gallery Section */}
        <section id="gallery" className="pt-24 pb-20">
          <h2 className="text-4xl font-bold text-center mb-8">
            {t('gallery.title')}
          </h2>
          <ImageCarousel />
        </section>

        {/* About Section */}
        <section id="about" className="pt-24 pb-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            {/* Toggle Buttons */}
            <div className="flex w-full max-w-4xl mx-auto mb-16 bg-white rounded-2xl shadow-lg overflow-hidden">
              <button
                onClick={() => setActiveTab('about')}
                className={`flex-1 py-4 px-6 text-lg font-semibold transition-all duration-300 ${
                  activeTab === 'about'
                    ? 'bg-primary text-gray-800'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('about.title')}
              </button>
              <button
                onClick={() => setActiveTab('vision')}
                className={`flex-1 py-4 px-6 text-lg font-semibold transition-all duration-300 ${
                  activeTab === 'vision'
                    ? 'bg-primary text-gray-800'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('about.vision.title')}
              </button>
            </div>

            {/* Content Area */}
            <div className="max-w-4xl mx-auto">
              {activeTab === 'about' ? (
                <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-primary/10">
                  <h2 className="text-3xl font-bold text-primary-dark mb-8 text-center">
                    {t('about.title')}
                  </h2>
                  <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                    <p>{t('about.description')}</p>
                    <p>{t('about.certified')}</p>
                    <p>{t('about.partnerships')}</p>
                    <p>{t('about.commitment')}</p>
                    <p className="font-semibold">{t('about.priority')}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-primary/10">
                  <h2 className="text-3xl font-bold text-primary-dark mb-8 text-center">
                    {t('about.vision.title')}
                  </h2>
                  <div className="space-y-12 text-lg text-gray-700">
                    {/* Vision */}
                    <div className="text-center mb-12">
                      <p>{t('about.vision.description')}</p>
                    </div>

                    {/* Core Values */}
                    <div>
                      <h3 className="text-2xl font-bold text-primary-dark mb-6 text-center">
                        {t('about.values.title')}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Compassion */}
                        <div className="bg-white rounded-2xl p-6 shadow-md border border-primary/10">
                          <h4 className="text-xl font-semibold text-primary-dark mb-3">
                            {t('about.values.compassion.title')}
                          </h4>
                          <p className="text-gray-600">
                            {t('about.values.compassion.description')}
                          </p>
                        </div>
                        {/* Integrity */}
                        <div className="bg-white rounded-2xl p-6 shadow-md border border-primary/10">
                          <h4 className="text-xl font-semibold text-primary-dark mb-3">
                            {t('about.values.integrity.title')}
                          </h4>
                          <p className="text-gray-600">
                            {t('about.values.integrity.description')}
                          </p>
                        </div>
                        {/* Excellence */}
                        <div className="bg-white rounded-2xl p-6 shadow-md border border-primary/10">
                          <h4 className="text-xl font-semibold text-primary-dark mb-3">
                            {t('about.values.excellence.title')}
                          </h4>
                          <p className="text-gray-600">
                            {t('about.values.excellence.description')}
                          </p>
                        </div>
                        {/* Collaboration */}
                        <div className="bg-white rounded-2xl p-6 shadow-md border border-primary/10">
                          <h4 className="text-xl font-semibold text-primary-dark mb-3">
                            {t('about.values.collaboration.title')}
                          </h4>
                          <p className="text-gray-600">
                            {t('about.values.collaboration.description')}
                          </p>
                        </div>
                        {/* Innovation */}
                        <div className="bg-white rounded-2xl p-6 shadow-md border border-primary/10">
                          <h4 className="text-xl font-semibold text-primary-dark mb-3">
                            {t('about.values.innovation.title')}
                          </h4>
                          <p className="text-gray-600">
                            {t('about.values.innovation.description')}
                          </p>
                        </div>
                        {/* Confidentiality */}
                        <div className="bg-white rounded-2xl p-6 shadow-md border border-primary/10">
                          <h4 className="text-xl font-semibold text-primary-dark mb-3">
                            {t('about.values.confidentiality.title')}
                          </h4>
                          <p className="text-gray-600">
                            {t('about.values.confidentiality.description')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Patient-Centered Approach */}

                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Staff Section */}
        <StaffSection />

        {/* Reviews Section */}
        <ReviewsSection />

        {/* Contact Section */}
        <section id="contact" className="min-h-screen pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-center text-primary-dark mb-12">
              {t('contact.title')}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-white rounded-3xl shadow-xl p-8">
                <h3 className="text-2xl font-bold text-primary-dark mb-6">
                  {t('contact.form.title')}
                </h3>
                <form 
                  className="space-y-6"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <label className="block text-gray-700 mb-2" htmlFor="name">
                      {t('contact.form.name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      placeholder={t('contact.form.namePlaceholder')}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2" htmlFor="email">
                      {t('contact.form.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      placeholder={t('contact.form.emailPlaceholder')}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2" htmlFor="phone">
                      {t('contact.form.phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      placeholder={t('contact.form.phonePlaceholder')}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2" htmlFor="message">
                      {t('contact.form.message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
                      placeholder={t('contact.form.messagePlaceholder')}
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-primary text-gray-800 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors"
                  >
                    {t('contact.form.submit')}
                  </button>
                </form>
              </div>
              {/* Contact Info & Hours */}
              <div className="space-y-8">
                {/* Contact Information */}
                <div className="bg-white rounded-3xl shadow-xl p-8">
                  <h3 className="text-2xl font-bold text-primary-dark mb-6">
                    {t('contact.info.title')}
                  </h3>
                  <div className="space-y-4">
                    <a
                      href="#"
                      onClick={handleWazeClick}
                      className={`w-full flex items-start gap-2 text-${dir === 'rtl' ? 'right' : 'left'} hover:bg-primary/5 p-2 -m-2 rounded-xl transition-colors`}
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-primary-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className={`flex-1 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                        <h4 className="font-semibold text-gray-900">
                          {t('contact.info.address')}
                        </h4>
                        <p className="text-gray-600">{t('contact.info.addressLine')}</p>
                      </div>
                    </a>
                    <div className="flex items-start gap-2">
                      <a 
                        href={`tel:${t('contact.info.phoneMain')}`}
                        className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 hover:bg-primary/20 transition-colors"
                      >
                        <svg className="w-4 h-4 text-primary-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </a>
                      <div className={`flex-1 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                        <h4 className="font-semibold text-gray-900">
                          {t('contact.info.phone')}
                        </h4>
                        <div className="space-y-1">
                          <a 
                            href={`tel:${t('contact.info.phoneMain')}`}
                            className="text-gray-600 hover:text-primary-dark transition-colors block"
                          >
                            {t('contact.info.phone')}: {t('contact.info.phoneMain')}
                          </a>
                          <a 
                            href={`tel:${t('contact.info.phoneAdditional')}`}
                            className="text-gray-600 hover:text-primary-dark transition-colors block"
                          >
                            {t('contact.info.phone')} ({t('contact.info.phoneAdditional') !== t('contact.info.phoneMain') ? t('contact.info.phoneAdditional') : ''})
                          </a>
                          <span className="text-gray-600 block">Fax: {t('contact.info.fax')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <a 
                        href="mailto:smc.shefaram@gmail.com"
                        className={`flex items-start gap-2 hover:bg-primary/5 p-2 -m-2 rounded-xl transition-colors ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-primary-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className={`flex-1 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                          <h4 className="font-semibold text-gray-900">
                            {t('contact.info.email')}
                          </h4>
                          <p className="text-gray-600">smc.shefaram@gmail.com</p>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
                {/* Opening Hours */}
                <div className="bg-white rounded-3xl shadow-xl p-8">
                  <h3 className="text-2xl font-bold text-primary-dark mb-6">
                    {t('contact.hours.title')}
                  </h3>
                  <div className="space-y-3">
                    {['sunday','monday','tuesday','wednesday','thursday','friday','saturday'].map((dayKey) => {
                      const { day, hours } = splitDayAndHours(t(`contact.hours.${dayKey}`));
                      return (
                        <div className="flex justify-between" key={dayKey}>
                          <span>{day}</span>
                          <span>{hours}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Section */}
        <section className="bg-primary text-gray-800 py-12">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Logo and Description */}
              <div className="lg:col-span-2">
                <div className="flex items-center mb-4">
                  <h3 className="text-2xl font-bold">{t('footer.centerName')}</h3>
                </div>
                <p className="text-gray-700 mb-6">
                  {t('footer.description')}
                </p>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h4>
                <ul className="space-y-2">
                  <li>
                    <a 
                      href="#contact" 
                      className="text-gray-700 hover:text-gray-900 transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      {t('footer.contactUs')}
                    </a>
                  </li>
                  <li>
                    <a 
                      href="#"
                      onClick={handleWazeClick}
                      className="text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      {t('footer.directions')}
                    </a>
                  </li>
                </ul>
              </div>

              {/* Social Media */}
              <div>
                <h4 className="text-lg font-semibold mb-4">{t('footer.followUs')}</h4>
                <div className="flex space-x-4 space-x-reverse">
                  <a 
                    href="https://www.instagram.com/smc.shefaram/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-700/10 rounded-full flex items-center justify-center hover:bg-gray-700/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://www.facebook.com/people/%D7%9E%D7%A8%D7%9B%D7%96-%D7%A8%D7%A4%D7%95%D7%90%D7%99-%D7%A9%D7%A4%D7%A8%D7%A2%D7%9D/61567775663869/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-700/10 rounded-full flex items-center justify-center hover:bg-gray-700/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="border-t border-gray-700/20 mt-8 pt-8 text-center">
              <p className="text-gray-700">
                {t('footer.copyright')}
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Home; 